import {
  isAllTestSkillStructure,
  normalizeConfiguredSkillLevelCount,
  SKILL_LEARNING_EMPTY_TEXT,
} from '../../../options/skill-learning.option';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  ViewChild,
} from '@angular/core';

import {
  SkillAvailableCurator,
  SkillGroup,
  SkillRequirementDeleteCommand,
  SkillRequirementUpdateCommand,
  SkillSpecializationResponse,
  SpecializationFormData,
  SpecializationFormInitialValue,
  SpecializationFormOptions,
  SpecializationFormValue,
} from '../../../models/skill-learning.model';
import {
  DEFAULT_SPECIALIZATION_FORM_DATA,
  DEFAULT_SPECIALIZATION_FORM_INITIAL_VALUE,
  DEFAULT_SPECIALIZATION_FORM_OPTIONS,
  SPECIALIZATION_CURATOR_SELECTION,
  SPECIALIZATION_FORM_SELECTION,
} from '../specialization-form/specialization-form.option';
import { SpecializationRequirementsEditorComponent } from '../specialization-requirements-editor/specialization-requirements-editor.component';

// TypeScript 2.3 needs a local alias to emit safe metadata for nullable interface inputs.
type NullableSpecialization = SkillSpecializationResponse | null;

@Component({
  selector: 'app-edit-specialization-general-page',
  templateUrl: './edit-specialization-general-page.component.html',
  styleUrls: ['./edit-specialization-general-page.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditSpecializationGeneralPageComponent implements OnChanges {

  @ViewChild(SpecializationRequirementsEditorComponent)
  public requirementsEditor: SpecializationRequirementsEditorComponent;

  @Input() public specialization: NullableSpecialization = null;
  @Input() public availableCurators: SkillAvailableCurator[] = [];
  @Input() public availableFillAssignees: SkillAvailableCurator[] = [];
  @Input() public availableGroups: SkillGroup[] = [];
  @Input() public canManageCurators = false;
  @Input() public canAssignAnyCuratorLevel = false;
  @Input() public canEditContent = false;
  @Input() public canManageParticipantGroups = false;
  @Input() public selectedDepartmentId = SKILL_LEARNING_EMPTY_TEXT;
  @Input() public isSubmitting = false;

  @Output() public generalInfoSaved = new EventEmitter<SpecializationFormValue>();
  @Output() public cancelRequested = new EventEmitter<void>();
  @Output() public requirementUpdated = new EventEmitter<SkillRequirementUpdateCommand>();
  @Output() public requirementDeleted = new EventEmitter<SkillRequirementDeleteCommand>();

  public formInitialValue: SpecializationFormInitialValue = DEFAULT_SPECIALIZATION_FORM_INITIAL_VALUE;
  public formData: SpecializationFormData = DEFAULT_SPECIALIZATION_FORM_DATA;
  public formOptions: SpecializationFormOptions = DEFAULT_SPECIALIZATION_FORM_OPTIONS;

  public get maxCurators(): number {
    return SPECIALIZATION_CURATOR_SELECTION.UNLIMITED;
  }

  public ngOnChanges(): void {
    this.formInitialValue = this.buildFormInitialValue();
    this.formData = this.buildFormData();
    this.formOptions = this.buildFormOptions();
  }

  public onFormSubmitted(value: SpecializationFormValue): void {
    this.generalInfoSaved.emit(value);
  }

  public onCancel(): void {
    this.cancelRequested.emit();
  }

  public onRequirementUpdated(command: SkillRequirementUpdateCommand): void {
    this.requirementUpdated.emit(command);
  }

  public onRequirementDeleted(command: SkillRequirementDeleteCommand): void {
    this.requirementDeleted.emit(command);
  }

  public hasDirtyDepartmentRequirement(): boolean {
    return !!this.requirementsEditor && this.requirementsEditor.hasDirtyDepartmentRequirement;
  }

  public discardDepartmentRequirementChanges(): void {
    if (this.requirementsEditor) {
      this.requirementsEditor.discardDepartmentChanges();
    }
  }

  public focusRequirements(): void {
    if (this.requirementsEditor) {
      this.requirementsEditor.focus();
    }
  }

  public finishRequirementPending(): void {
    if (this.requirementsEditor) {
      this.requirementsEditor.finishPending();
    }
  }

  private buildFormInitialValue(): SpecializationFormInitialValue {
    const specialization = this.specialization;
    if (!specialization) {
      return Object.assign({}, DEFAULT_SPECIALIZATION_FORM_INITIAL_VALUE);
    }
    return Object.assign({}, DEFAULT_SPECIALIZATION_FORM_INITIAL_VALUE, {
      name: specialization.name,
      description: specialization.description,
      groupId: this.canManageParticipantGroups
        ? specialization.groupId
        : specialization.curatorGroupId || specialization.groupId,
      fillAssigneeId: specialization.fillAssigneeId || SPECIALIZATION_FORM_SELECTION.EMPTY_VALUE,
      crossDepartmental: specialization.crossDepartmental,
      participantGroupIds: specialization.participantGroupIds || [],
      responsibleGroupByDepartmentId: specialization.responsibleGroupByDepartmentId || {},
      responsibleGroupId: specialization.curatorGroupId || SPECIALIZATION_FORM_SELECTION.EMPTY_VALUE,
      curators: specialization.curators || [],
    });
  }

  private buildFormData(): SpecializationFormData {
    return {
      manageableCuratorGroupIds: this.specialization
        ? this.specialization.manageableCuratorGroupIds || [] : [],
      availableGroups: this.availableGroups,
      availableCurators: this.availableCurators,
      availableFillAssignees: this.availableFillAssignees,
    };
  }

  private buildFormOptions(): SpecializationFormOptions {
    return Object.assign({}, DEFAULT_SPECIALIZATION_FORM_OPTIONS, {
      showCurators: this.canManageCurators,
      showCrossDepartmentalOption: this.canManageParticipantGroups,
      showCrossDepartmentCurators: this.canManageCurators && !!this.specialization && this.specialization.crossDepartmental,
      responsibleGroupsLocked: !!this.specialization && this.specialization.crossDepartmental,
      requiredResponsibleGroupId: this.specialization
        ? this.specialization.owningDepartmentId || this.specialization.groupId : SKILL_LEARNING_EMPTY_TEXT,
      isSubmitting: this.isSubmitting,
      requireCurator: this.requiresCurator(),
      maxCurators: this.maxCurators,
      canAssignAnyCuratorLevel: this.canAssignAnyCuratorLevel,
      configuredLevelCount: this.specialization
        ? normalizeConfiguredSkillLevelCount(this.specialization.configuredLevelCount) : null,
      contentEditable: this.canEditContent,
    });
  }

  private requiresCurator(): boolean {
    const value = this.specialization;
    return this.canManageCurators && !!value && !value.crossDepartmental
      && !isAllTestSkillStructure(
        normalizeConfiguredSkillLevelCount(value.configuredLevelCount),
        value.configuredLevel2Type,
        value.configuredLevel3Type,
      );
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/skill-learning/components/admin/edit-specialization-general-page/edit-specialization-general-page.component.ts